import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useChatSocket from "../lib/useChatSocket";
import SafetyBanner from "../components/SafetyBanner";
import { TID } from "../constants/catalog";
import { Send, MessageSquare, ChevronLeft, Loader2, Radio } from "lucide-react";

const POLL_MS = 5000; // fallback polling when socket is disconnected

export default function Chats() {
  const { chatId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) nav("/login", { state: { redirect: chatId ? `/chats/${chatId}` : "/chats" } });
  }, [user, loading, chatId, nav]);

  // Load chat list
  useEffect(() => {
    if (!user) return;
    api.get("/chats").then((r) => setChats(r.data || []));
  }, [user, chatId]);

  // Initial load when chat changes
  useEffect(() => {
    if (!chatId) {
      setActive(null);
      setMessages([]);
      return;
    }
    let stop = false;
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/chats/${chatId}/messages`);
        if (stop) return;
        setActive(r.data.chat);
        setMessages(r.data.messages);
      } catch (err) {
        if (err?.response?.status === 404) nav("/chats");
      }
    };
    fetchOnce();
    return () => {
      stop = true;
    };
  }, [chatId, nav]);

  // WebSocket live updates
  const wsUrl = chatId && process.env.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${chatId}`
    : null;
  const onSocketMessage = useCallback((data) => {
    if (data?.type === "message" && data.message) {
      setMessages((m) => (m.some((x) => x.message_id === data.message.message_id) ? m : [...m, data.message]));
    }
  }, []);
  const { connected } = useChatSocket(wsUrl, onSocketMessage);

  // Polling fallback only when socket is disconnected
  useEffect(() => {
    if (!chatId || connected) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/chats/${chatId}/messages`);
        setMessages(r.data.messages);
      } catch {}
    }, POLL_MS);
    return () => clearInterval(t);
  }, [chatId, connected]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const content = text.trim();
    if (!content || !chatId) return;
    setSending(true);
    try {
      const r = await api.post(`/chats/${chatId}/messages`, { content });
      setMessages((m) => [...m, r.data]);
      setText("");
    } finally {
      setSending(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="pbp-section py-6 md:py-10">
      <div className="grid lg:grid-cols-12 gap-6 h-[calc(100vh-180px)] min-h-[520px]">
        <aside data-testid={TID.chat.list} className={`lg:col-span-4 pbp-card overflow-hidden flex flex-col ${chatId ? "hidden lg:flex" : "flex"}`}>
          <div className="px-5 py-4 pbp-hairline flex items-center gap-2">
            <MessageSquare className="w-4 h-4" strokeWidth={1.6} />
            <span className="font-semibold">Conversations</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {chats.length === 0 ? (
              <div className="p-6 text-sm text-[#86868b]">No chats yet. Start one from a listing.</div>
            ) : (
              chats.map((c) => {
                const other = c.buyer_id === user.user_id ? { name: c.seller_name, pic: c.seller_picture } : { name: c.buyer_name, pic: c.buyer_picture };
                return (
                  <Link
                    key={c.chat_id}
                    to={`/chats/${c.chat_id}`}
                    data-testid={TID.chat.item(c.chat_id)}
                    className={`flex gap-3 items-center px-5 py-3 border-b border-black/5 hover:bg-[#f5f5f7] ${c.chat_id === chatId ? "bg-[#0071e3]/8" : ""}`}
                  >
                    {other.pic ? (
                      <img src={other.pic} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#0071e3] grid place-items-center text-white font-semibold">
                        {other.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{other.name}</div>
                      <div className="text-xs text-[#86868b] truncate">{c.last_message || c.listing_title}</div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </aside>

        <main className={`lg:col-span-8 pbp-card overflow-hidden flex flex-col ${chatId ? "flex" : "hidden lg:flex"}`}>
          {!chatId ? (
            <div className="flex-1 grid place-items-center text-[#86868b] text-sm">Select a conversation</div>
          ) : !active ? (
            <div className="flex-1 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#0071e3]" /></div>
          ) : (
            <>
              <SafetyBanner />
              <div className="px-5 py-3 pbp-hairline flex items-center gap-3">
                <button onClick={() => nav("/chats")} className="lg:hidden pbp-btn-ghost -ml-2">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {(() => {
                  const other = active.buyer_id === user.user_id ? { name: active.seller_name, pic: active.seller_picture } : { name: active.buyer_name, pic: active.buyer_picture };
                  return (
                    <>
                      {other.pic ? (
                        <img src={other.pic} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#0071e3] grid place-items-center text-white text-sm font-semibold">
                          {other.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                          {other.name}
                          {connected && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#34c759]">
                              <Radio className="w-3 h-3" strokeWidth={2} /> live
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#86868b] truncate">{active.listing_title}</div>
                      </div>
                      <Link className="pbp-btn-ghost text-xs" to={`/listing/${active.listing_id}`}>View listing</Link>
                    </>
                  );
                })()}
              </div>

              <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#fafafd]">
                {messages.map((m) => (
                  <Message key={m.message_id} m={m} me={user.user_id} />
                ))}
              </div>

              <form onSubmit={send} className="px-4 py-3 border-t border-black/5 flex gap-2">
                <input
                  data-testid={TID.chat.input}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message (text only — no files)"
                  className="pbp-input"
                  maxLength={2000}
                />
                <button data-testid={TID.chat.send} disabled={sending || !text.trim()} className="pbp-btn-primary px-5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const Message = ({ m, me }) => {
  if (m.is_system) {
    return (
      <div className="text-center text-xs text-[#9a4e00] bg-[#ff9500]/10 rounded-xl px-4 py-2 max-w-xl mx-auto">
        {m.content}
      </div>
    );
  }
  const mine = m.sender_id === me;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-[14.5px] leading-relaxed shadow-sm ${mine ? "bg-[#0071e3] text-white rounded-br-sm" : "bg-white border border-black/5 rounded-bl-sm"}`}>
        {m.content}
        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#86868b]"}`}>
          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
};
